import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  getStoredConsent,
  saveConsent,
  consumeOpenRequest,
  subscribeCookieConsent,
} from '@/lib/cookieConsent'
import { useCookieConfig, useRecordCookieConsent } from '@/hooks/useCompliance'
import type { CookieCategory } from '@/types'

// Fallback categories if the backend config is unavailable. Essential is always
// on and cannot be disabled (2.13.4-010). Labels are resolved via i18n by key,
// falling back to any backend-provided label.
const FALLBACK_CATEGORIES: CookieCategory[] = [
  { key: 'essential', label: 'Essential cookies', required: true },
  { key: 'analytics', label: 'Analytics cookies', required: false },
  { key: 'marketing', label: 'Marketing cookies', required: false },
]

// 2.13.4 — Cookie consent banner. Shown on first visit (no stored choice) and
// reopenable via Settings > Cookie Settings. No non-essential cookies
// are set before the user makes a choice; choices are timestamped.
export function CookieConsentBanner() {
  const { t, i18n } = useTranslation('compliance')
  // Prefer a localized label/description by category key; fall back to the
  // backend-provided label (or empty description) for unknown categories.
  const categoryTranslationKey = (c: CookieCategory) =>
    c.key === 'necessary' ? 'essential' : c.key
  const categoryLabel = (c: CookieCategory) => {
    const key = `cookie.categories.${categoryTranslationKey(c)}`
    return i18n.exists(key, { ns: 'compliance' }) ? t(key) : c.label
  }
  const categoryDescription = (c: CookieCategory) => {
    const key = `cookie.descriptions.${categoryTranslationKey(c)}`
    return i18n.exists(key, { ns: 'compliance' }) ? t(key) : ''
  }
  const { data } = useCookieConfig()
  const record = useRecordCookieConsent()
  const categories = data?.data?.categories ?? FALLBACK_CATEGORIES

  const [visible, setVisible] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selections, setSelections] = useState<Record<string, boolean>>({})

  // Initialise visibility from stored consent + subscribe to Settings reopen.
  useEffect(() => {
    const sync = () => {
      if (consumeOpenRequest()) {
        seedSelections()
        setShowModal(true)
        setVisible(true)
        return
      }
      const stored = getStoredConsent()
      if (!stored) {
        seedSelections()
        setVisible(true)
      }
    }
    sync()
    return subscribeCookieConsent(sync)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function seedSelections() {
    const stored = getStoredConsent()
    const next: Record<string, boolean> = {}
    for (const c of categories) {
      next[c.key] = c.required ? true : (stored?.selections[c.key] ?? false)
    }
    setSelections(next)
  }

  function persist(finalSelections: Record<string, boolean>) {
    saveConsent(finalSelections)
    record.mutate({
      consent_type: 'cookie',
      granted: Object.values(finalSelections).some(Boolean),
      selections: finalSelections,
    })
    setVisible(false)
    setShowModal(false)
  }

  function acceptAll() {
    const next: Record<string, boolean> = {}
    for (const c of categories) next[c.key] = true
    persist(next)
  }

  function rejectAll() {
    const next: Record<string, boolean> = {}
    for (const c of categories) next[c.key] = c.required
    persist(next)
  }

  function savePreferences() {
    const next = { ...selections }
    for (const c of categories) if (c.required) next[c.key] = true
    persist(next)
  }

  function onModalOpenChange(open: boolean) {
    setShowModal(open)
    if (!open) setVisible(getStoredConsent() === null)
  }

  if (!visible) return null

  return (
    <>
      {!showModal && (
        <div className='fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background p-4 shadow-lg'>
          <div className='mx-auto flex max-w-5xl flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between'>
            <p className='text-sm text-muted-foreground'>
              {t('cookie.bannerText')}
            </p>
            <div className='flex shrink-0 flex-wrap items-center gap-2'>
              <Button
                variant='ghost'
                onClick={() => {
                  seedSelections()
                  setShowModal(true)
                }}
              >
                {t('cookie.managePreferences')}
              </Button>
              <Button variant='outline' onClick={rejectAll}>
                {t('cookie.rejectAll')}
              </Button>
              <Button onClick={acceptAll}>{t('cookie.acceptAll')}</Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={onModalOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cookie.settingsTitle')}</DialogTitle>
          </DialogHeader>
          <div className='space-y-4'>
            {categories.map(c => (
              <div
                key={c.key}
                className='flex items-start justify-between gap-4 rounded-md border border-border p-3'
              >
                <div>
                  <Label htmlFor={`cookie-${c.key}`} className='font-medium'>
                    {categoryLabel(c)}
                  </Label>
                  <p className='text-xs text-muted-foreground'>
                    {categoryDescription(c)}
                  </p>
                </div>
                <Switch
                  id={`cookie-${c.key}`}
                  checked={c.required ? true : (selections[c.key] ?? false)}
                  disabled={c.required}
                  onCheckedChange={v =>
                    setSelections(s => ({ ...s, [c.key]: v }))
                  }
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={rejectAll}>
              {t('cookie.rejectAll')}
            </Button>
            <Button onClick={savePreferences}>
              {t('cookie.savePreferences')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CookieConsentBanner
